/**
 * OrderEarmarksSection — dedicated-batch reservations on the Order Detail page.
 *
 * Shown only to ADMIN and OPERATIONS roles.
 *
 * Features:
 *   - List all earmarks for the order (with batch number, variant, qty, status)
 *   - "Pin batch" modal: pick a variant → pick an ACTIVE batch with freely-
 *     available units, enter quantity
 *   - "Unpin" action: release an ACTIVE earmark back to the general pool
 *
 * "Only from this batch" (ship whole order from one batch):
 *   An ACTIVE earmark can be marked exclusive. When exclusive, WZ release
 *   fulfils that variant ONLY from the order's pinned batches — the general FIFO
 *   fallback to older batches is skipped, so the old batch stays untouched. The
 *   ordered quantity is unchanged ("tyle ile chce klient"). If the pinned stock
 *   is insufficient, WZ release fails loudly instead of silently mixing older
 *   batches. Toggled per-earmark via the action below.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  App,
  Button,
  Card,
  Form,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { CarOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { POLISH_DECIMAL_INPUT_NUMBER_PROPS } from '../../../utils/polishNumberInput';
import api from '../../../services/api';
import type { ProductVariant } from '../../../types/pz';
import {
  buildProductSelectorSearchText,
  getProductSelectorDimensions,
  getProductSelectorName,
} from '../../../utils/productSelectorLabel';
import {
  createEarmark,
  fetchOrderEarmarks,
  releaseEarmark,
  setEarmarkExclusive,
  type OrderEarmark,
  type EarmarkStatus,
} from '../api/orderEarmarksApi';

const { Text } = Typography;

// ---------------------------------------------------------------------------
// Status tag colours
// ---------------------------------------------------------------------------

const STATUS_COLORS: Record<EarmarkStatus, string> = {
  ACTIVE: 'blue',
  FULFILLED: 'green',
  RELEASED: 'default',
};

// ---------------------------------------------------------------------------
// Types for inventory data fetched locally
// ---------------------------------------------------------------------------

interface InventoryRow {
  productVariantId: string;
  productVariant: ProductVariant;
}

interface BatchRow {
  id: string;
  batchNumber: string;
  purchaseDate: string | null;
  quantityRemaining: number;
  status: string;
  unitCostPLN?: number | null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface OrderEarmarksSectionProps {
  orderId: string;
  onChanged?: () => void | Promise<void>;
}

const OrderEarmarksSection: React.FC<OrderEarmarksSectionProps> = ({ orderId, onChanged }) => {
  const { t } = useTranslation(['orders', 'common']);
  const { message, modal } = App.useApp();

  const [earmarks, setEarmarks] = useState<OrderEarmark[]>([]);
  const [loading, setLoading] = useState(false);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalSubmitting, setModalSubmitting] = useState(false);
  const [form] = Form.useForm();

  // Batch picker state inside modal
  const [inventoryList, setInventoryList] = useState<InventoryRow[]>([]);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null);
  const [batches, setBatches] = useState<BatchRow[]>([]);
  const [batchesLoading, setBatchesLoading] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState<BatchRow | null>(null);

  // ---------------------------------------------------------------------------
  // Data fetching
  // ---------------------------------------------------------------------------

  const loadEarmarks = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchOrderEarmarks(orderId);
      setEarmarks(data);
    } catch {
      message.error(t('orders:orderDetail.earmarkFetchError'));
    } finally {
      setLoading(false);
    }
  }, [orderId, message, t]);

  useEffect(() => {
    loadEarmarks();
  }, [loadEarmarks]);

  // Fetch inventory list when modal opens
  const loadInventory = async () => {
    setInventoryLoading(true);
    try {
      const res = await api.get<InventoryRow[]>('/inventory');
      setInventoryList(res.data);
    } catch {
      message.error(t('common:error'));
    } finally {
      setInventoryLoading(false);
    }
  };

  // Fetch active batches when a variant is selected
  const loadBatches = async (variantId: string) => {
    setBatchesLoading(true);
    setBatches([]);
    setSelectedBatch(null);
    form.setFieldValue('inventoryBatchId', undefined);
    try {
      const res = await api.get<BatchRow[]>(`/inventory/variant/${variantId}/batches`);
      // Filter to ACTIVE batches with remaining qty
      setBatches(res.data.filter(b => b.status === 'ACTIVE' && b.quantityRemaining > 0));
    } catch {
      message.error(t('common:error'));
    } finally {
      setBatchesLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Modal handlers
  // ---------------------------------------------------------------------------

  const openModal = () => {
    form.resetFields();
    setSelectedVariantId(null);
    setBatches([]);
    setSelectedBatch(null);
    setModalOpen(true);
    loadInventory();
  };

  const handleVariantChange = (variantId: string) => {
    setSelectedVariantId(variantId);
    loadBatches(variantId);
  };

  const handleBatchChange = (batchId: string) => {
    const batch = batches.find(b => b.id === batchId) || null;
    setSelectedBatch(batch);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setModalSubmitting(true);
      await createEarmark(orderId, {
        inventoryBatchId: values.inventoryBatchId,
        quantity: values.quantity,
      });
      message.success(t('orders:orderDetail.earmarkCreated'));
      setModalOpen(false);
      await loadEarmarks();
      await onChanged?.();
    } catch (err: any) {
      if (err?.response?.data?.message) {
        message.error(err.response.data.message);
      } else if (err?.errorFields) {
        // Form validation error — antd will show inline, no toast needed
      } else {
        message.error(t('orders:orderDetail.earmarkError'));
      }
    } finally {
      setModalSubmitting(false);
    }
  };

  const handleRelease = (earmark: OrderEarmark) => {
    modal.confirm({
      title: t('orders:orderDetail.earmarkConfirmUnpin'),
      content: t('orders:orderDetail.earmarkConfirmUnpinDetail'),
      okType: 'danger',
      onOk: async () => {
        try {
          await releaseEarmark(orderId, earmark.id);
          message.success(t('orders:orderDetail.earmarkReleased'));
          await loadEarmarks();
          await onChanged?.();
        } catch {
          message.error(t('orders:orderDetail.earmarkError'));
        }
      },
    });
  };

  const applyExclusive = async (earmark: OrderEarmark, next: boolean) => {
    try {
      await setEarmarkExclusive(orderId, earmark.id, next);
      message.success(
        next
          ? t('orders:orderDetail.earmarkExclusiveOn')
          : t('orders:orderDetail.earmarkExclusiveOff')
      );
      await loadEarmarks();
      await onChanged?.();
    } catch {
      message.error(t('orders:orderDetail.earmarkError'));
    }
  };

  const handleToggleExclusive = (earmark: OrderEarmark) => {
    if (earmark.isExclusive) {
      // Turning OFF needs no warning — it just restores normal FIFO fallback.
      applyExclusive(earmark, false);
      return;
    }
    // Turning ON changes consumption behaviour — confirm with an explanation.
    modal.confirm({
      title: t('orders:orderDetail.earmarkExclusiveConfirmTitle'),
      content: t('orders:orderDetail.earmarkExclusiveConfirmDetail'),
      okText: t('orders:orderDetail.earmarkExclusiveConfirmOk'),
      cancelText: t('common:cancel'),
      onOk: () => applyExclusive(earmark, true),
    });
  };

  // ---------------------------------------------------------------------------
  // Table columns
  // ---------------------------------------------------------------------------

  const columns = [
    {
      title: t('orders:orderDetail.earmarkColBatch'),
      dataIndex: 'batchNumber',
      key: 'batchNumber',
      render: (v: string) => <Text code>{v}</Text>,
    },
    {
      title: t('orders:orderDetail.earmarkColVariant'),
      key: 'variant',
      render: (_: unknown, r: OrderEarmark) => (
        <Space size={6} wrap>
          <span>{r.variantSku} – {r.variantName}</span>
          {r.isExclusive && (
            <Tooltip title={t('orders:orderDetail.earmarkExclusiveHint')}>
              <Tag color="gold" icon={<CarOutlined />} style={{ marginInlineEnd: 0 }}>
                {t('orders:orderDetail.earmarkExclusiveTag')}
              </Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: t('orders:orderDetail.earmarkColPurchaseDate'),
      dataIndex: 'purchaseDate',
      key: 'purchaseDate',
      render: (v: string | null) => v ? dayjs(v).format('YYYY-MM-DD') : '—',
    },
    {
      title: t('orders:orderDetail.earmarkColQty'),
      dataIndex: 'quantity',
      key: 'quantity',
    },
    {
      title: t('orders:orderDetail.earmarkColConsumed'),
      dataIndex: 'consumedQuantity',
      key: 'consumedQuantity',
    },
    {
      title: t('orders:orderDetail.earmarkColStatus'),
      dataIndex: 'status',
      key: 'status',
      render: (status: EarmarkStatus) => (
        <Tag color={STATUS_COLORS[status]}>
          {t(`orders:orderDetail.earmarkStatus_${status}`)}
        </Tag>
      ),
    },
    {
      title: t('orders:orderDetail.earmarkColActions'),
      key: 'actions',
      render: (_: unknown, r: OrderEarmark) =>
        r.status === 'ACTIVE' ? (
          <Space size={4}>
            <Tooltip
              title={
                r.isExclusive
                  ? t('orders:orderDetail.earmarkExclusiveToggleOff')
                  : t('orders:orderDetail.earmarkExclusiveToggleOn')
              }
            >
              <Button
                icon={<CarOutlined />}
                size="small"
                type={r.isExclusive ? 'primary' : 'default'}
                onClick={() => handleToggleExclusive(r)}
              />
            </Tooltip>
            <Tooltip title={t('orders:orderDetail.earmarkUnpin')}>
              <Button
                icon={<DeleteOutlined />}
                size="small"
                danger
                onClick={() => handleRelease(r)}
              />
            </Tooltip>
          </Space>
        ) : null,
    },
  ];

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <Card
      title={t('orders:orderDetail.earmarks')}
      extra={
        <Button
          type="primary"
          icon={<PlusOutlined />}
          size="small"
          onClick={openModal}
        >
          {t('orders:orderDetail.earmarkBatch')}
        </Button>
      }
      style={{ marginTop: 16 }}
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        {t('orders:orderDetail.earmarksSubtitle')}
      </Text>

      <div style={{ width: '100%', maxWidth: '100%', overflowX: 'auto' }}>
        <Table<OrderEarmark>
          rowKey="id"
          columns={columns}
          dataSource={earmarks}
          loading={loading}
          size="small"
          pagination={false}
          scroll={{ x: 760 }}
          locale={{ emptyText: t('orders:orderDetail.earmarkNoData') }}
        />
      </div>

      {/* Pin batch modal */}
      <Modal
        open={modalOpen}
        title={t('orders:orderDetail.earmarkBatchTitle')}
        onCancel={() => setModalOpen(false)}
        onOk={handleSubmit}
        okText={t('orders:orderDetail.earmarkBatchPin')}
        cancelText={t('common:cancel')}
        confirmLoading={modalSubmitting}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="variantId"
            label={t('orders:orderDetail.earmarkBatchVariant')}
            rules={[{ required: true, message: t('orders:orderDetail.earmarkSelectVariantFirst') }]}
          >
            <Select
              loading={inventoryLoading}
              showSearch
              optionFilterProp="searchText"
              onChange={handleVariantChange}
              placeholder={t('orders:orderDetail.earmarkSelectVariantFirst')}
              options={inventoryList.map((inventory) => {
                const name = getProductSelectorName(
                  inventory.productVariant,
                  t('orders:orderDetail.product'),
                );
                const dimensions = getProductSelectorDimensions(inventory.productVariant);

                return {
                  value: inventory.productVariantId,
                  label: [name, dimensions].filter(Boolean).join(' – '),
                  searchText: buildProductSelectorSearchText(inventory.productVariant),
                };
              })}
            />
          </Form.Item>

          <Form.Item
            name="inventoryBatchId"
            label={t('orders:orderDetail.earmarkBatchBatch')}
            rules={[{ required: true }]}
          >
            <Select
              loading={batchesLoading}
              disabled={!selectedVariantId}
              onChange={handleBatchChange}
              placeholder={
                selectedVariantId
                  ? t('orders:orderDetail.earmarkBatchBatch')
                  : t('orders:orderDetail.earmarkSelectVariantFirst')
              }
              options={batches.map(b => ({
                value: b.id,
                label: `${b.batchNumber} — ${t('orders:orderDetail.earmarkBatchAvailable')}: ${b.quantityRemaining}`,
              }))}
            />
          </Form.Item>

          {selectedBatch && (
            <Space style={{ marginBottom: 8, display: 'block' }}>
              <Text type="secondary">
                {t('orders:orderDetail.earmarkBatchAvailable')}: <strong>{selectedBatch.quantityRemaining}</strong>
              </Text>
            </Space>
          )}

          <Form.Item
            name="quantity"
            label={t('orders:orderDetail.earmarkBatchQty')}
            rules={[
              { required: true },
              {
                validator: (_, value) => {
                  if (!value || value <= 0) {
                    return Promise.reject(new Error('Must be > 0'));
                  }
                  if (selectedBatch && value > selectedBatch.quantityRemaining) {
                    return Promise.reject(
                      new Error(`Max: ${selectedBatch.quantityRemaining}`)
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber
              min={0.001}
              max={selectedBatch?.quantityRemaining ?? undefined}
              step={0.001}
              precision={3}
              style={{ width: '100%' }}
              {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default OrderEarmarksSection;
